import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface FoundationalTaskTag {
  id: string;
  brand_id: string;
  name: string;
  color: string;
  created_at: string;
  updated_at: string;
}

export const useFoundationalTaskTags = (brandId: string | undefined) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: tags = [], isLoading } = useQuery({
    queryKey: ['foundational-task-tags', brandId],
    queryFn: async () => {
      if (!brandId) return [];

      const { data, error } = await supabase
        .from('foundational_task_tags')
        .select('*')
        .eq('brand_id', brandId)
        .order('name');

      if (error) throw error;
      return data as FoundationalTaskTag[];
    },
    enabled: !!brandId,
  });

  const createTag = useMutation({
    mutationFn: async (newTag: { name: string; color: string }) => {
      if (!brandId) throw new Error('Brand ID is required');

      const { data, error } = await supabase
        .from('foundational_task_tags')
        .insert({
          brand_id: brandId,
          name: newTag.name,
          color: newTag.color,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['foundational-task-tags', brandId] });
      toast({
        title: "Success",
        description: "Tag created successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create tag",
        variant: "destructive",
      });
      console.error('Error creating tag:', error);
    },
  });

  const updateTag = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<FoundationalTaskTag> }) => {
      const { data, error } = await supabase
        .from('foundational_task_tags')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onMutate: async ({ id, updates }) => {
      await queryClient.cancelQueries({ queryKey: ['foundational-task-tags', brandId] });
      const previousTags = queryClient.getQueryData(['foundational-task-tags', brandId]);
      
      queryClient.setQueryData(['foundational-task-tags', brandId], (old: FoundationalTaskTag[] = []) => 
        old.map(tag => tag.id === id ? { ...tag, ...updates } : tag)
      );
      
      return { previousTags };
    },
    onError: (error, variables, context) => {
      if (context?.previousTags) {
        queryClient.setQueryData(['foundational-task-tags', brandId], context.previousTags);
      }
      toast({
        title: "Error",
        description: "Failed to update tag",
        variant: "destructive",
      });
      console.error('Error updating tag:', error);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['foundational-task-tags', brandId] });
    },
  });

  const deleteTag = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('foundational_task_tags')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['foundational-task-tags', brandId] });
      const previousTags = queryClient.getQueryData(['foundational-task-tags', brandId]);
      
      queryClient.setQueryData(['foundational-task-tags', brandId], (old: FoundationalTaskTag[] = []) => 
        old.filter(tag => tag.id !== id)
      );
      
      return { previousTags };
    },
    onError: (error, variables, context) => {
      if (context?.previousTags) {
        queryClient.setQueryData(['foundational-task-tags', brandId], context.previousTags);
      }
      toast({
        title: "Error",
        description: "Failed to delete tag",
        variant: "destructive",
      });
      console.error('Error deleting tag:', error);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['foundational-task-tags', brandId] });
    },
  });

  return {
    tags,
    isLoading,
    createTag,
    updateTag,
    deleteTag,
  };
};
